import {
    getLogger,
    createActionFactory,
    Feature,
    createThunk,
    getMatchingFavoritePerson,
    PersonType,
    FavoritePerson,
    ThunkDispatch,
    contactsLeftNavItemId,
    Person,
} from '../../../shared';
import { getAppState, getCurrentWritableContactFolderId, getPackageState } from '../../../appstate';
import { getPersonasInFolder } from '../data/personaActions';
import { HubState, packageStateKey } from '../../state';
import { getCurrentHubPersona } from '../../selectors/currentPersonaSelector';
import { convertContactToPersonType, Contact } from 'owa-contacts-api';
import { isFeatureEnabled } from 'owa-feature-flags';

export const contactEditorCancelClickedAction = createActionFactory<void>(
    'ContactEditorCancelClickedAction',
    Feature.ContactManagement
);

export const contactEditorNewContactSaveClickedAction = createActionFactory<{
    personaId?: string;
    folderId: string;
    hubContact?: Person;
}>('ContactEditorNewContactSavedClickedAction', Feature.ContactManagement);

export const contactEditorExistingContactSaveClickedAction = createActionFactory<{
    personaId?: string;
    folderId: string;
    hubContact?: Person;
}>('ContactEditorExistingContactSaveClickedAction', Feature.ContactManagement);

export const contactEditorFavoriteSavedAction = createActionFactory<{
    matchingFavorite: FavoritePerson;
    personaId: string;
}>('ContactEditorFavoriteSavedAction', Feature.ContactManagement);

const getOwaMailFavoritesStore = () =>
    import(/*OwaMailFavoritesStore */ 'owa-mail-favorites-store');

const getMatchingFavoriteIfExisting = (rootState: {}): FavoritePerson | undefined => {
    const state = getPackageState<HubState>(rootState, packageStateKey);
    const favorites = state.appState.data.hubState.favoritePersonsState.favoritePersons;
    const currentPersona = getCurrentHubPersona(state.appState, state.packageState);

    if (!currentPersona) {
        return undefined;
    }

    if (currentPersona.type === PersonType.FavoritePerson) {
        return favorites.find(
            favorite =>
                favorite.type === PersonType.FavoritePerson &&
                favorite.hubPersonaId === currentPersona.hubPersonaId
        ) as FavoritePerson;
    } else if (currentPersona.type === PersonType.Person) {
        return getMatchingFavoritePerson(
            currentPersona.personaId,
            currentPersona.info.mainEmailAddress,
            favorites
        );
    }

    return undefined;
};

// Updates the persisted favorite state in owa-mail for the edited contact if they a favorite.
const updateMatchingFavoriteIfExisting = (matchingFavorite: FavoritePerson): Promise<void> =>
    getOwaMailFavoritesStore().then(module => {
        if (matchingFavorite && !isFeatureEnabled('tri-favorites-roaming')) {
            return module.lazyOnFavoritePersonaUpdated
                .importAndExecute(matchingFavorite.hubPersonaId)
                .catch(e =>
                    getLogger().log('ErrorUpdatingMatchingFavorite: ', { errorMessage: e.message })
                );
        }

        return Promise.resolve();
    });

export const contactEditorNewContactSaveClicked = (savedContactOrPersonaId: Contact | string) =>
    createThunk(
        'ContactEditorNewContactSaveClicked',
        (dispatch: ThunkDispatch, getState: () => {}): void => {
            // If we receive contact object, it means we are on new LPE and vice versa
            const hubContact =
                typeof savedContactOrPersonaId === 'object'
                    ? convertContactToPersonType(savedContactOrPersonaId)
                    : undefined;

            const personaId =
                typeof savedContactOrPersonaId === 'object'
                    ? hubContact?.personaId
                    : savedContactOrPersonaId;

            const rootState = getState();
            const appState = getAppState(rootState);
            const isCAPIv3Enabled = appState.view.layoutState.isCAPIv3Enabled;

            const folderId = isCAPIv3Enabled
                ? contactsLeftNavItemId
                : getCurrentWritableContactFolderId(appState);
            const matchingFavorite = getMatchingFavoriteIfExisting(rootState);

            dispatch(
                contactEditorNewContactSaveClickedAction({
                    personaId,
                    folderId,
                    hubContact,
                })
            );

            // Unfortunately we need to give some breathing room so that FindPeople can find the new Persona by email address.
            const thirtySecondsInMilliseconds = 30000;
            setTimeout(
                () => updateMatchingFavoriteIfExisting(matchingFavorite),
                thirtySecondsInMilliseconds
            );

            // Always re-fetch personas in the folder where the contact was saved as we will be navigating to this folder
            if (isCAPIv3Enabled) {
                /* Without user folders, we will redirect to "Your/All contacts" view which is populated with the contacts
                in the root contact folder */
                getPersonasInFolder(dispatch)(
                    appState.data.contactFoldersState.rootContactFolder.id
                );
            } else {
                getPersonasInFolder(dispatch)(folderId);
            }

            if (matchingFavorite) {
                dispatch(contactEditorFavoriteSavedAction({ matchingFavorite, personaId }));
            }
        }
    );

export const contactEditorExistingContactSaveClicked = (
    savedContactOrPersonaId?: Contact | string
) =>
    createThunk(
        'ContactEditorExistingContactSaveClicked',
        (dispatch: ThunkDispatch, getState: () => {}): void => {
            // If we receive contact object, it means we are on new LPE and vice versa
            const hubContact =
                typeof savedContactOrPersonaId === 'object'
                    ? convertContactToPersonType(savedContactOrPersonaId)
                    : undefined;

            const personaId =
                typeof savedContactOrPersonaId === 'object'
                    ? hubContact?.personaId
                    : savedContactOrPersonaId;
            const rootState = getState();
            const appState = getAppState(rootState);
            const isCAPIv3Enabled = appState.view.layoutState.isCAPIv3Enabled;

            const folderId = isCAPIv3Enabled
                ? contactsLeftNavItemId
                : getCurrentWritableContactFolderId(appState);
            const matchingFavorite = getMatchingFavoriteIfExisting(rootState);

            dispatch(
                contactEditorExistingContactSaveClickedAction({
                    personaId,
                    folderId,
                    hubContact,
                })
            );

            // TODO: invoke onPersonaUpdated from 'owa-persona/lib/services/onPersonaUpdated'
            // for updating the cache for the contact photo if changed. New LPE does not
            // support editing photos currently, hence skipped for now.

            updateMatchingFavoriteIfExisting(matchingFavorite);

            // below flow is not needed for old contact editor as it uses
            // another callback, onPersonaUpdated, from initializeLpc.ts
            if (hubContact && matchingFavorite) {
                dispatch(contactEditorFavoriteSavedAction({ matchingFavorite, personaId }));
            }
        }
    );

export type { Action } from '../../../shared';
